import styled from 'styled-components';

import { getNotificationVisual } from 'constants/notificationTypes';
import { relativeTimeFromNow } from 'lib/date';
import Notification from 'models/Notification';
import { colors } from 'styles/theme/colors';
import { BodySmall, Label } from 'styles/typography';

type Props = {
  notification: Notification;
  // A ticking "now" (from useNow) so the relative timestamp advances on its own cadence.
  now: Date;
  // Open the row's target: mark read (if unread) + SPA-navigate, in one action (§5 item 3).
  onOpen: (notification: Notification) => void;
  // Per-row read/unread toggle (§5 item 5). Does not navigate.
  onToggleRead: (notification: Notification) => void;
};

export default function NotificationRow({ notification, now, onOpen, onToggleRead }: Props) {
  const { notificationEvent: event, isUnread } = notification;
  const { Icon, color, background } = getNotificationVisual(event.kind);

  return (
    <Row $unread={isUnread} data-testid='notificationRow'>
      <ChipColumn>
        <Chip style={{ backgroundColor: background }}>
          <Icon width={12} height={12} color={color} />
        </Chip>
        {isUnread && <UnreadDot data-testid='unreadDot' />}
      </ChipColumn>

      <Body>
        <Message>{event.message}</Message>
        <TargetLink type='button' onClick={() => onOpen(notification)} data-testid='notificationLink'>
          {event.linkLabel}
        </TargetLink>
        <Timestamp>{relativeTimeFromNow(notification.createdAt, now)}</Timestamp>
      </Body>

      <MarkButton type='button' onClick={() => onToggleRead(notification)} data-testid='markToggle'>
        {isUnread ? 'Mark read' : 'Mark unread'}
      </MarkButton>
    </Row>
  );
}

const Row = styled.div<{ $unread: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 18px 24px;
  background: ${({ $unread }) => ($unread ? colors.blue3 : colors.white)};
  border-bottom: 1px solid ${colors.black10};
`;

const ChipColumn = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Chip = styled.div`
  align-items: center;
  border-radius: 4px;
  display: flex;
  height: 24px;
  justify-content: center;
  width: 24px;
`;

const UnreadDot = styled.span`
  background: ${colors.blue100};
  border-radius: 50%;
  bottom: -2px;
  height: 8px;
  position: absolute;
  right: -2px;
  width: 8px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1;
`;

const Message = styled(BodySmall)`
  color: ${colors.black100};
  font-size: 12px;
  line-height: 20px;
  margin: 0;
`;

const TargetLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  color: ${colors.blue100};
  cursor: pointer;
  font-size: 12px;
  line-height: 12px;
  padding: 0;
  text-align: left;
  text-decoration: underline;
`;

const Timestamp = styled(Label)`
  color: ${colors.black50};
`;

const MarkButton = styled.button`
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  color: ${colors.black75};
  cursor: pointer;
  flex-shrink: 0;
  font-size: 12px;
  height: 24px;
  min-width: 84px;
  padding: 0 10px;
  white-space: nowrap;

  &:hover {
    background: ${colors.black3};
  }
`;
